import React, { useState, useEffect } from 'react';
import { categoriesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Tag,
  Plus,
  Trash2,
  Loader2,
  Lock
} from 'lucide-react';
import Layout from '../../components/Layout';
import { TranslatableSpan } from '../../components/TranslatableText';

const PRESET_COLORS = [
  '#F97316', '#EAB308', '#22C55E', '#3B82F6', '#8B5CF6', 
  '#EF4444', '#EC4899', '#14B8A6', '#6366F1', '#A1A1AA'
];

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', color: '#F97316' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data);
    } catch (err) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    try {
      const response = await categoriesAPI.create(newCategory);
      setCategories([...categories, response.data]);
      setNewCategory({ name: '', description: '', color: '#F97316' });
      setDialogOpen(false);
      toast.success('Category created');
    } catch (err) {
      toast.error('Failed to create category');
    }
  };

  const handleDelete = async (id) => {
    try {
      await categoriesAPI.delete(id);
      setCategories(categories.filter(c => c.id !== id));
      toast.success('Category deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete category');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  const predefinedCategories = categories.filter(c => c.is_predefined);
  const customCategories = categories.filter(c => !c.is_predefined);

  return (
    <Layout>
      <div className="space-y-6" data-testid="categories-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white flex items-center gap-3">
              <Tag className="w-10 h-10 text-amber-500" />
              Categories
            </h1>
            <p className="text-slate-400 mt-1">Organize tasks by maintenance type</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-category-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700">
              <DialogHeader>
                <DialogTitle className="text-white">Add Category</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-400">Name *</Label>
                  <Input
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                    className="input-industrial mt-1"
                    placeholder="e.g., Structural"
                    data-testid="category-name-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-400">Description</Label>
                  <Input
                    value={newCategory.description}
                    onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                    className="input-industrial mt-1"
                    placeholder="Brief description"
                  />
                </div>
                <div>
                  <Label className="text-slate-400">Color</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {PRESET_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setNewCategory({ ...newCategory, color })}
                        className={`w-8 h-8 rounded-sm border-2 transition-all ${
                          newCategory.color === color ? 'border-white scale-110' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
                <Button onClick={handleCreate} className="btn-primary w-full" data-testid="save-category-btn">
                  Create Category
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Predefined Categories */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Lock className="w-4 h-4 text-slate-400" />
            System Categories
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {predefinedCategories.map((category) => (
              <div 
                key={category.id}
                className="card-industrial flex items-center gap-4"
                data-testid={`category-${category.id}`}
              >
                <div 
                  className="w-12 h-12 rounded-sm flex items-center justify-center"
                  style={{ backgroundColor: `${category.color}20` }}
                >
                  <Tag className="w-6 h-6" style={{ color: category.color }} />
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium"><TranslatableSpan text={category.name} /></p>
                  <p className="text-slate-400 text-sm"><TranslatableSpan text={category.description} /></p>
                </div>
                <Lock className="w-4 h-4 text-slate-500" />
              </div>
            ))}
          </div>
        </div>

        {/* Custom Categories */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-3">Custom Categories</h2>
          {customCategories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customCategories.map((category) => (
                <div 
                  key={category.id}
                  className="card-industrial flex items-center gap-4"
                  data-testid={`category-${category.id}`}
                >
                  <div 
                    className="w-12 h-12 rounded-sm flex items-center justify-center"
                    style={{ backgroundColor: `${category.color}20` }}
                  >
                    <Tag className="w-6 h-6" style={{ color: category.color }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{category.name}</p>
                    <p className="text-slate-400 text-sm">{category.description || 'No description'}</p>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button className="bg-red-500/20 hover:bg-red-500/30 text-red-400 p-2">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-slate-900 border-slate-700">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Delete Category</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                          Are you sure you want to delete {category.name}?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(category.id)} className="bg-red-500 hover:bg-red-600 text-white">
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state py-12 border border-dashed border-slate-700 rounded-sm">
              <Tag className="w-12 h-12 text-slate-600 mb-3" />
              <p className="text-slate-400">No custom categories yet</p>
              <p className="text-slate-500 text-sm">Add categories specific to your organization</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CategoriesPage;
